import React from 'react';
import { isSafari } from '@/utils/browser-kit';
import { type TextFragment } from '@/utils/text-kit';
import { TextAlignType, TextWritingType, type ILayerProps, type ITextLayerProps } from '@/version-types';
import { getFontFamilySubset } from '../../utils';
import { GlowUse, ShadowUse, StrokeUse, TextUse } from '../uses';
import { VerticalGradientDisplay } from './vertical-gradient-display';
import { VerticalTextureDisplay } from './vertical-texture-display';

/**
 * HACK: 竖排文字场景下，暂时使用 clipPath 的方式实现渐变填充和纹理填充
 * 因为在竖排文字下，比如中文等字符不会发生旋转
 * 比如“中”字，在竖排文字下并不会顺时针旋转90度，
 * 从而导致渐变填充和纹理填充无法正常显示
 * 但在纯英文的竖排文字下，字符会顺时针旋转90度，渐变或纹理填充可以正常显示
 *
 * HACK: 当竖排文字且是 safari 浏览器时，需要使用逐字渲染方式解决排版错误的问题（浏览器兼容性）
 *
 * HACK: 当文本对齐方式为 justify 时，需要使用逐字渲染方式解决无法实现两端对齐的排版问题（无排版引擎的兜底写法，此时会丢失阿拉伯字符的连字效果）
 */

type Props = {
    layer: ILayerProps;
    contentFragments: TextFragment[];
};

const doc = document.documentElement;

export const ContentRenderer: React.FC<Props> = ({ layer, contentFragments }) => {
    const { id } = layer;
    const layerData = layer.data as ITextLayerProps;
    const {
        writingMode,
        texture,
        textGradient,
        underline,
        lineThrough,
        italic,
        bold,
        letterSpacing,
        fontSize,
        color,
        textAlign,
        lineSpacing,
    } = layerData;
    const fontFamily = getFontFamilySubset(layer);

    const isVertical = writingMode !== TextWritingType.HORIZONTAL_TB;
    const isUsingGradient = textGradient && textGradient.colors.length > 1;
    const isUsingTexture = !!texture && !!texture.url;

    const textElements = contentFragments.map((item, idx) => {
        const { style } = item;

        const x = doc.scrollLeft + (isVertical ? item.left + item.width : item.left || 0);
        const y = doc.scrollTop + (item.top || 0);

        const textDecoration = (() => {
            if ((texture && texture.url) || (textGradient && textGradient.colors.length > 1)) return 'none'; // 纹理填充或渐变填充不应用 text-decoration
            if (style) {
                if (style.underline) return 'underline';
                if (style.lineThrough) return 'line-through';
                return 'none';
            }
            if (underline) return 'underline';
            if (lineThrough) return 'line-through';
            return 'none';
        })();
        const fontStyle = style ? (style.italic ? 'italic' : 'normal') : italic ? 'italic' : 'normal';
        const fontWeight = style ? (style.bold ? 'bold' : 'normal') : bold ? 'bold' : 'normal';
        const fragmentFontFamily = style?.fontFamily || fontFamily;
        const fragmentFontSize = style?.fontSize || fontSize;

        const fill = isUsingGradient || isUsingTexture ? undefined : style?.color || color;
        const rotate =
            typeof style?.rotate === 'undefined'
                ? undefined
                : `rotate(${style.rotate}, ${item.left + item.width / 2}, ${item.top + item.height / 2})`;

        const hackSafariVerticalText = isSafari && isVertical;
        const hackJustifyAlign = textAlign === TextAlignType.JUSTIFY;

        const children = (() => {
            if (!item.children || item.children.length === 0) return item.text;

            if (hackSafariVerticalText) {
                return item.children.map((child, childIdx) => {
                    const x = child.left + child.width / 2;
                    const y = child.top - (lineSpacing - 1) * fontSize * 0.5;

                    return (
                        <tspan x={x} y={y} key={`fragment-${idx}-${childIdx}`}>
                            {child.text}
                        </tspan>
                    );
                });
            }

            if (hackJustifyAlign) {
                return item.children.map((child, childIdx) => {
                    const x = isVertical ? child.width + child.left : child.left;
                    const y = isSafari ? child.top + fontSize : child.top; // HACK: Safari 下，需要将 y 坐标向下偏移一个 fontSize 的高度，否则会显示不正常（该 hack 只是近似实现，无法保证在所有场景下都能正常显示）
                    return (
                        <tspan x={x} y={y} key={`fragment-${idx}-${childIdx}`}>
                            {child.text}
                        </tspan>
                    );
                });
            }

            return item.text;
        })();

        return (
            <text
                xmlnsXlink="http://www.w3.org/1999/xlink"
                xmlSpace="preserve"
                id={`text-${id}-${idx}`}
                key={`text-${id}-${idx}`}
                x={x}
                y={y}
                letterSpacing={letterSpacing}
                writingMode={writingMode}
                dominantBaseline="text-before-edge"
                fontFamily={fragmentFontFamily}
                fontSize={fragmentFontSize}
                textDecoration={textDecoration}
                fontStyle={fontStyle}
                fontWeight={fontWeight}
                fill={fill}
                transform={rotate}
            >
                {children}
            </text>
        );
    });

    if (isUsingGradient && isVertical) {
        return <VerticalGradientDisplay layer={layer} textElements={textElements} />;
    }

    if (isUsingTexture && isVertical) {
        return <VerticalTextureDisplay layer={layer} textElements={textElements} />;
    }

    return (
        <React.Fragment>
            <defs>
                <g id={`text-${id}`}>{textElements}</g>
            </defs>

            <GlowUse href={`#text-${id}`} layer={layer} />
            <ShadowUse href={`#text-${id}`} layer={layer} />
            <StrokeUse href={`#text-${id}`} layer={layer} />
            <TextUse href={`#text-${id}`} layer={layer} />
        </React.Fragment>
    );
};
